import type { ChartPanel } from 'features/charts-v2/types/filter-hash/v2'

/**
 * The panel fields a chart query reads — the client mirror of the backend's
 * `ChartQueryPanel`.
 *
 * Everything else on `ChartPanel` is identity or display state. Those fields
 * matter: they round-trip through the URL hash so a shared link restores its
 * own font size and legend, and `POST /project/hash-filter` must keep receiving
 * the WHOLE panel. They just have no bearing on a query result, and the backend
 * has never read them off a request body.
 */
export type ChartQueryPanel = Pick<
  ChartPanel,
  | 'chartType'
  | 'metrics'
  | 'breakdowns'
  | 'filters'
  | 'dateRange'
  | 'timeBucket'
  | 'searchTerms'
  | 'searchCriteria'
>

/** The filter half of a wire panel: what a page sends for one filter layer. */
export type WireFilterFields = Pick<
  ChartQueryPanel,
  'filters' | 'searchTerms' | 'searchCriteria'
>

/**
 * One dashboard widget tile's request body — the backend `WindowedFilterFields` the
 * widget routes (`WIDGET_ROUTES` in the widget feature) take: the filter half
 * plus a REQUIRED `dateRange`
 * (`{startDate: 0, endDate: 0}` means no window).
 *
 * The backend model is STRICT (`extra="forbid"`) at the top level, so any other
 * key — a forwarded `smartSearchEnabled`, a chart field — is a 422 for every
 * tile of that kind.
 */
export type WidgetFiltersBody = WireFilterFields &
  Pick<ChartQueryPanel, 'dateRange'>

/** The `WidgetFiltersBody` keys as data, so a test can pin the STRICT key set. */
export const WIDGET_FILTERS_FIELDS = [
  'filters',
  'dateRange',
  'searchTerms',
  'searchCriteria',
] as const satisfies readonly (keyof WidgetFiltersBody)[]

/**
 * The field list, as data, so a test can assert the projection emits exactly
 * these and nothing else.
 *
 * It cannot be checked against the backend's `ChartQueryPanel` from here — that
 * would need the generated OpenAPI types, which this repo regenerates by hand
 * off a running server. The backend pins its own side (its two panel models are
 * asserted field-for-field); the two lists agreeing is a convention, and adding
 * a query field means editing both.
 */
export const CHART_QUERY_PANEL_FIELDS = [
  'chartType',
  'metrics',
  'breakdowns',
  'filters',
  'dateRange',
  'timeBucket',
  'searchTerms',
  'searchCriteria',
] as const satisfies readonly (keyof ChartQueryPanel)[]

/**
 * A panel whose client-only state has been lowered into wire fields.
 *
 * The brand exists to make one ordering mistake impossible rather than
 * documented. `smartSearchEnabled` is a client-only flag that reaches the
 * backend as a `SEMANTIC` entry in `searchCriteria` — so the translation
 * READS a field `toQueryPanel` drops and WRITES one it keeps. Run the
 * projection first and the flag is gone before anything can read it.
 *
 * Nothing about that failure is visible: `injectSemanticIntoPanel` no-ops on a
 * panel with no flag, which is exactly what a stripped panel looks like. The
 * result is a valid panel describing a keyword search where the user asked for
 * a semantic one — a wrong query rather than a missing field.
 *
 * So `toQueryPanel` takes this type and nothing else. The two correct call
 * sites already injected first and needed no change; the wrong order is now a
 * compile error. Panels arriving already wire-shaped — from storage, or from a
 * caller that types them `unknown` — say so with `assumeWireReady`.
 */
export type WireReadyPanel = ChartPanel & {
  readonly __wireReady: unique symbol
}

/**
 * Assert a panel is already wire-shaped, without translating anything.
 *
 * For panels that never held client-only state to begin with: ones read back
 * from storage (the marker was written before saving) or ones a caller holds
 * as `unknown` and cannot narrow. Legitimate, and worth being a named call
 * rather than a cast — it makes the claim greppable, and it makes a NEW call
 * site choose deliberately instead of inheriting someone else's `as never`.
 *
 * If the panel does carry live client state, this silently skips the
 * translation. Use `injectSemanticIntoPanel` instead.
 */
export function assumeWireReady(panel: ChartPanel): WireReadyPanel {
  return panel as WireReadyPanel
}

/**
 * Project a panel down to the fields a query reads, for sending over the wire.
 *
 * Apply this at the HTTP boundary, not where a panel is built: the store, the
 * URL hash and every intermediate transform keep working with whole panels, and
 * a caller cannot forget to narrow. Today the backend drops the surplus keys
 * anyway (`extra="ignore"`); narrowing here is what lets it eventually reject
 * them instead, which is the only way a typo'd field name gets caught.
 *
 * Note the asymmetry with `smartSearchEnabled`: it is a client-only flag and is
 * NOT a query field, but it reaches the wire indirectly, as a `SEMANTIC` entry
 * that `injectSemanticIntoPanel` writes into `searchCriteria`. Running this
 * first would drop the flag before anything could read it — so the parameter
 * is `WireReadyPanel`, which only `injectSemanticIntoPanel` and the explicit
 * `assumeWireReady` produce. The ordering used to be a docstring warning and a
 * test; it is a compile error now.
 */
export function toQueryPanel(panel: WireReadyPanel): ChartQueryPanel {
  return {
    chartType: panel.chartType,
    metrics: panel.metrics,
    breakdowns: panel.breakdowns,
    // A drill-down's client-only `groupBucket` never reaches the legacy wire.
    filters: panel.filters.map(({ groupBucket: _bucket, ...filter }) => filter),
    dateRange: panel.dateRange,
    timeBucket: panel.timeBucket,
    searchTerms: panel.searchTerms,
    searchCriteria: panel.searchCriteria,
  }
}
